import { INITIAL_KID_STATE } from "../components/WebUi/Pages/AddStudent/ValidationSchema";
import { mapStudentToKid } from "../components/WebUi/Pages/AddStudent/helper";

const DRAFT_PREFIX = "draft_kids_";

/**
 * Extracts the current parent user ID from localStorage or context object.
 * Checks userFromContext, parentLocalData, user (AuthContext), tenant-user, and student.
 * @param {Object} [userFromContext]
 * @returns {string|number|null}
 */
export const getParentId = (userFromContext = null) => {
  try {
    if (userFromContext?.user?.id) return userFromContext.user.id;
    if (userFromContext?.id) return userFromContext.id;

    // 1. Check parentLocalData (used when student switches to parent)
    const parentLocalDataRaw = localStorage.getItem("parentLocalData");
    if (parentLocalDataRaw) {
      const p = JSON.parse(parentLocalDataRaw);
      if (p?.user?.id) return p.user.id;
      if (p?.id) return p.id;
    }

    // 2. Check user (used when parent is logged in directly)
    const authData = localStorage.getItem("user");
    if (authData) {
      const u = JSON.parse(authData);
      if (u?.user?.id) return u.user.id;
      if (u?.id) return u.id;
    }

    // 3. Check tenant-user
    const tenantUser = localStorage.getItem("tenant-user");
    if (tenantUser) {
      const t = JSON.parse(tenantUser);
      if (t?.user?.id) return t.user.id;
      if (t?.id) return t.id;
    }

    // 4. Check student session createdBy
    const studentData = localStorage.getItem("student");
    if (studentData) {
      const s = JSON.parse(studentData);
      if (s?.student?.createdBy) return s.student.createdBy;
      if (s?.createdBy) return s.createdBy;
    }

    return null;
  } catch (err) {
    console.warn("Failed to get parentId:", err);
    return null;
  }
};

/**
 * Gets the localStorage key for a specific parent account.
 * Falls back to "guest" if parentId cannot be determined.
 * @param {string|number|null} parentId
 * @returns {string}
 */
export const getDraftKey = (parentId) => {
  const pid = parentId || getParentId() || "guest";
  return `${DRAFT_PREFIX}${pid}`;
};

/**
 * Checks if a kid form object has any user-entered content.
 * @param {Object} kid
 * @returns {boolean}
 */
export const isKidFilled = (kid) => {
  if (!kid) return false;
  return Boolean(
    kid.firstName?.trim() ||
    kid.lastName?.trim() ||
    kid.username?.trim() ||
    kid.age ||
    kid.email?.trim() ||
    kid.password,
  );
};

/**
 * Checks if there is any user-added content in the kids list.
 * Returns true if more than 1 kid card exists or any field is typed.
 * @param {Array} kids
 * @returns {boolean}
 */
export const hasAnyDraftContent = (kids) => {
  if (!Array.isArray(kids) || kids.length === 0) return false;
  return kids.length > 1 || kids.some(isKidFilled);
};

/**
 * Saves uncommitted draft kids to localStorage, scoped by parentId.
 * Only saves kids that are marked as "new" and have at least some data entered.
 * @param {string|number|null} parentId
 * @param {Object} values - Formik values ({ kids, progressAssignedTo })
 */
export const saveStudentDraft = (parentId, values) => {
  const key = getDraftKey(parentId);
  if (!key || !values?.kids || !Array.isArray(values.kids)) return;

  // Filter only new kids
  const newKids = values.kids.filter((k) => k._status === "new");

  // If there are no new kids or none have any data, clear storage
  const hasContent = newKids.some(isKidFilled);
  if (!hasContent) {
    localStorage.removeItem(key);
    return;
  }

  const payload = {
    updatedAt: Date.now(),
    kids: newKids,
    progressAssignedTo: values.progressAssignedTo ?? null,
  };

  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (err) {
    console.warn("Failed to save student draft to localStorage:", err);
  }
};

/**
 * Loads student draft from localStorage for a specific parentId.
 * @param {string|number|null} parentId
 * @param {number} [maxAgeMs=86400000] - Default 24 hours TTL
 * @returns {Object|null}
 */
export const loadStudentDraft = (parentId, maxAgeMs = 86400000) => {
  const key = getDraftKey(parentId);
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.kids)) {
      localStorage.removeItem(key);
      return null;
    }

    // Check expiration if timestamp exists
    if (data.updatedAt && Date.now() - data.updatedAt > maxAgeMs) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (err) {
    console.warn("Failed to load student draft from localStorage:", err);
    return null;
  }
};

/**
 * Clears the draft for a specific parent.
 * @param {string|number|null} parentId
 */
export const clearStudentDraft = (parentId) => {
  const key = getDraftKey(parentId);
  if (key) {
    localStorage.removeItem(key);
  }
};

const PAID_PARENT_DRAFT_PREFIX = "paid_parent_kids_draft_";

/**
 * Gets the localStorage key for paid parent kids registration draft.
 * Format: paid_parent_kids_draft_${parentId}
 * @param {string|number|null} parentId
 * @returns {string|null}
 */
export const getPaidParentDraftKey = (parentId) => {
  const pid = parentId || getParentId() || "guest";
  return `${PAID_PARENT_DRAFT_PREFIX}${pid}`;
};

/**
 * Saves paid parent draft kids to localStorage, scoped by parentId.
 * Also persists maxKidsAllowed, kidCount, kidSubscribed, and tiersCount to detect multi-device changes.
 * @param {string|number|null} parentId
 * @param {Object} draftData - { kids, maxKidsAllowed, kidCount, kidSubscribed, tiersCount }
 */
export const savePaidParentDraft = (parentId, draftData) => {
  const key = getPaidParentDraftKey(parentId);
  if (!key || !draftData?.kids || !Array.isArray(draftData.kids)) return;

  const hasContent = hasAnyDraftContent(draftData.kids);
  if (!hasContent) {
    localStorage.removeItem(key);
    return;
  }

  const payload = {
    updatedAt: Date.now(),
    maxKidsAllowed: draftData.maxKidsAllowed ?? null,
    kidCount: draftData.kidCount ?? null,
    kidSubscribed: draftData.kidSubscribed ?? null,
    tiersCount: draftData.tiersCount ?? null,
    kids: draftData.kids,
  };

  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (err) {
    console.warn("Failed to save paid parent draft to localStorage:", err);
  }
};

/**
 * Loads paid parent draft from localStorage.
 * @param {string|number|null} parentId
 * @param {number} [maxAgeMs=86400000] - 24 hours TTL
 * @returns {Object|null}
 */
export const loadPaidParentDraft = (parentId, maxAgeMs = 86400000) => {
  const key = getPaidParentDraftKey(parentId);
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.kids)) {
      localStorage.removeItem(key);
      return null;
    }

    if (data.updatedAt && Date.now() - data.updatedAt > maxAgeMs) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (err) {
    console.warn("Failed to load paid parent draft from localStorage:", err);
    return null;
  }
};

/**
 * Clears the paid parent draft for a specific parent.
 * @param {string|number|null} parentId
 */
export const clearPaidParentDraft = (parentId) => {
  const key = getPaidParentDraftKey(parentId);
  if (key) {
    localStorage.removeItem(key);
  }
};

/**
 * Clears all draft_kids_* and paid_parent_kids_draft_* keys from localStorage (e.g. on logout).
 */
export const clearAllStudentDrafts = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith(DRAFT_PREFIX) || k.startsWith(PAID_PARENT_DRAFT_PREFIX))
      ) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch (err) {
    console.warn("Failed to clear all student drafts:", err);
  }
};

/**
 * Reconciles server-registered students with uncommitted local drafts.
 * Ensures the total number of students never exceeds maxStudents (default 5).
 *
 * @param {Array} rawServerStudents - Students returned by API (/parent/onboarding/students)
 * @param {Object|null} draftData - Draft object loaded from localStorage
 * @param {number} [maxStudents=5] - Maximum allowed students per account
 * @returns {{
 *   reconciledKids: Array,
 *   overflowCount: number,
 *   duplicateCount: number,
 *   reconciledProgressAssignedTo: number|null
 * }}
 */
export const reconcileDraftWithServer = (
  rawServerStudents = [],
  draftData = null,
  maxStudents = 5,
) => {
  const serverKids = (rawServerStudents || []).map(mapStudentToKid);
  const availableSlots = Math.max(0, maxStudents - serverKids.length);

  // If no draft exists or no new kids in draft
  const draftKids = (draftData?.kids || []).filter((k) => k._status === "new");

  if (draftKids.length === 0) {
    return {
      reconciledKids:
        serverKids.length > 0 ? serverKids : [{ ...INITIAL_KID_STATE }],
      overflowCount: 0,
      duplicateCount: 0,
      reconciledProgressAssignedTo: null,
    };
  }

  // Deduplicate draft kids against existing server kids
  const existingUsernames = new Set(
    serverKids.map((k) => k.username?.trim().toLowerCase()).filter(Boolean),
  );
  const existingEmails = new Set(
    serverKids.map((k) => k.email?.trim().toLowerCase()).filter(Boolean),
  );

  let duplicateCount = 0;
  const uniqueDraftKids = draftKids.filter((draft) => {
    const u = draft.username?.trim().toLowerCase();
    const e = draft.email?.trim().toLowerCase();

    const isDup =
      (u && existingUsernames.has(u)) || (e && existingEmails.has(e));
    if (isDup) {
      duplicateCount++;
      return false;
    }
    return true;
  });

  // Clamp drafts to available slots
  let validDrafts = [];
  let overflowCount = 0;

  if (availableSlots <= 0) {
    overflowCount = uniqueDraftKids.length;
    validDrafts = [];
  } else if (uniqueDraftKids.length > availableSlots) {
    overflowCount = uniqueDraftKids.length - availableSlots;
    validDrafts = uniqueDraftKids.slice(0, availableSlots);
  } else {
    validDrafts = uniqueDraftKids;
  }

  const reconciledKids = [...serverKids, ...validDrafts];

  // Resolve progressAssignedTo:
  // 1. If any kid already has transferred progress, select that one
  const transferredIndex = reconciledKids.findIndex(
    (k) => k.has_transferred_progress === true,
  );
  let reconciledProgressAssignedTo = null;

  if (transferredIndex !== -1) {
    reconciledProgressAssignedTo = transferredIndex;
  } else if (
    draftData?.progressAssignedTo != null &&
    draftData.progressAssignedTo >= 0 &&
    draftData.progressAssignedTo < reconciledKids.length
  ) {
    reconciledProgressAssignedTo = draftData.progressAssignedTo;
  }

  return {
    reconciledKids:
      reconciledKids.length > 0 ? reconciledKids : [{ ...INITIAL_KID_STATE }],
    overflowCount,
    duplicateCount,
    reconciledProgressAssignedTo,
  };
};
